import {
  Flex,
  IconButton,
  IconEditLine,
  IconSubaccountsLine,
  IconSubaccountsSolid,
  IconTrashLine,
  Popover,
  Text,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import Skeleton from "react-loading-skeleton";
import ProgramAddIcon from "../../CustomIcons/ProgramAdd";
import { AcademicHierarchyService } from "../../services/AcademicHierarchyService";
import { ProgramService } from "../../services/ProgramService";
import { UserDetailContext } from "../../utils/Context";
import { sort } from "../../utils/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo/DialogYesNo";
import ProgramsModal from "../Programs/ProgramsModal";
import { DepartmentSection } from "./DepartmentSection";
import Program from "./Program";

const Department = ({ department, parent, onEditDepartmentCallback }) => {
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const [departmentData, setDepartmentData] = useState(department);
  const [removeSubjectDialogOpen, setRemoveSubjectDialogOpen] = useState(false);
  const [subjectToRemove, setSubjectToRemove] = useState({});
  const [subjectParent, setSubjectParent] = useState({});
  const [openAddDepartment, setOpenAddDepartment] = useState(false);
  const [openAddProgram, setOpenAddProgram] = useState(false);
  const [loading, setLoading] = useState(true);
  const [departmentExpanded, setDepartmentExpanded] = useState(false);

  const handleCreateProgram = (newProgram) => {
    ProgramService.createProgram(newProgram)
      .then((p) => {
        setDepartmentData((prevData) => ({
          ...prevData,
          programs: prevData.programs ? [...prevData.programs, p] : [],
        }));
        setOpenAddProgram(false);
        handleOnToggle(true);
        showNotification(
          t("notifications.general.addSuccess", { obj: t("programs.program") })
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.general.addError", { obj: t("programs.program") }),
          ERROR
        );
      });
  };

  const showRemoveSubjectConfirmation = (subjectToRemove, parent) => {
    setSubjectToRemove(subjectToRemove);
    setSubjectParent(parent);
    setRemoveSubjectDialogOpen(true);
  };

  const handleRemoveDepartment = async () => {
    const index = subjectParent.subArray.findIndex(
      (sub) => sub.ddl_id === subjectToRemove.ddl_id
    );
    if (index >= 0) {
      subjectParent.subArray.splice(index, 1);
    }

    const newSubjectList = [];
    subjectParent.subArray.forEach((subject) => {
      if (subject.label === "is academic subject") {
        newSubjectList.push(subject.ddl_id);
      }
    });
    await AcademicHierarchyService.setSubjects(subjectParent, newSubjectList)
      .then(() => {
        showNotification(
          t("notifications.general.deleteSuccess", {
            obj: t("academicHierarchy.department"),
          })
        );
        setSubjectToRemove({});
        setSubjectParent({});
        setRemoveSubjectDialogOpen(false);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", {
            obj: t("academicHierarchy.department"),
          }),
          ERROR
        );
      });
  };

  const departmentModalCallBack = (newDepartment) => {
    setDepartmentData((prevData) => ({
      ...prevData,
      name: newDepartment.name,
      title: newDepartment.title,
      creation_method: newDepartment.creation_method,
    }));
    if (onEditDepartmentCallback) {
      onEditDepartmentCallback(newDepartment);
    }
  };

  const handleOnToggle = (expanded) => {
    setDepartmentExpanded(expanded);
    if (!departmentData.programs) {
      ProgramService.getProgramsByAcSub(departmentData.ddl_id)
        .then((programs) => {
          programs = sort(programs, (p) => p.degreeName);
          setDepartmentData((prevData) => ({
            ...prevData,
            programs: programs,
          }));
          setLoading(false);
        })
        .catch(() => {
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("programs.programs"),
            }),
            ERROR
          );
        });
    } else {
      setLoading(false);
    }
  };

  const departmentPermissions = () => {
    return (
      userDetails.isAC() || userDetails.ac_subjects.includes(department.title)
    );
  };

  return (
    <>
      <ToggleGroup
        size="small"
        border={false}
        toggleLabel={t("academicHierarchy.toggleDepartment")}
        iconExpanded={IconSubaccountsSolid}
        icon={IconSubaccountsLine}
        expanded={departmentExpanded}
        onToggle={(_e, expanded) => handleOnToggle(expanded)}
        summary={
          <Flex alignItems="center" gap="small">
            <Flex.Item>
              <Text>{departmentData.name}</Text>
            </Flex.Item>
            <Flex.Item shouldGrow textAlign="end">
              {userDetails.isAC() && (
                <Popover
                  isShowingContent={openAddDepartment}
                  onShowContent={() => {
                    setOpenAddDepartment(true);
                  }}
                  onHideContent={() => {
                    setOpenAddDepartment(false);
                  }}
                  id={`edit-department-popover-${departmentData.ddl_id}`}
                  placement="start top"
                  renderTrigger={
                    <Tooltip
                      color="primary-inverse"
                      renderTip={t("academicHierarchy.editDepartment")}
                    >
                      <IconButton
                        id={`edit-department-${departmentData.ddl_id}`}
                        renderIcon={IconEditLine}
                        screenReaderLabel={t(
                          "academicHierarchy.editDepartment"
                        )}
                      />
                    </Tooltip>
                  }
                  on="click"
                  screenReaderLabel={t(
                    "academicHierarchy.editDepartmentPopover"
                  )}
                  mountNode={() => document.getElementById("main")}
                >
                  <View padding="small" display="block" width={500}>
                    <DepartmentSection
                      setOpenSection={setOpenAddDepartment}
                      upsertHandler={departmentModalCallBack}
                      department={departmentData}
                    />
                  </View>
                </Popover>
              )}
              {departmentPermissions() && (
                <Tooltip
                  color="primary-inverse"
                  renderTip={t("academicHierarchy.addProgram")}
                >
                  <IconButton
                    id={`add-program-${departmentData.ddl_id}`}
                    renderIcon={ProgramAddIcon}
                    onClick={() => setOpenAddProgram(true)}
                    screenReaderLabel={t("academicHierarchy.addProgram")}
                    margin="0 0 0 small"
                  />
                </Tooltip>
              )}
              {userDetails.isAC() && (
                <Tooltip
                  color="primary-inverse"
                  renderTip={t("academicHierarchy.removeDepartment")}
                >
                  <IconButton
                    id={`remove-level-${departmentData.ddl_id}`}
                    renderIcon={IconTrashLine}
                    onClick={() =>
                      showRemoveSubjectConfirmation(departmentData, parent)
                    }
                    screenReaderLabel={t("academicHierarchy.removeDepartment")}
                    margin="0 0 0 small"
                  />
                </Tooltip>
              )}
            </Flex.Item>
          </Flex>
        }
      >
        <div style={{ paddingLeft: 12 }}>
          {!loading ? (
            departmentData.programs && departmentData.programs.length > 0 ? (
              departmentData.programs.map((program) => (
                <Program
                  key={program.id}
                  program={program}
                  parent={departmentData}
                  setParent={setDepartmentData}
                  editPermission={departmentPermissions()}
                />
              ))
            ) : (
              <div style={{ marginLeft: 25, padding: 10 }}>
                {t("academicHierarchy.noPrograms")}
              </div>
            )
          ) : (
            <Skeleton style={{ height: 64, borderRadius: 5 }} />
          )}
        </div>
      </ToggleGroup>
      <ProgramsModal
        open={openAddProgram}
        setOpen={setOpenAddProgram}
        department={departmentData}
        title={t("academicHierarchy.addProgram")}
        service={handleCreateProgram}
        program={{ name: "", accreditor: "" }}
      />
      <DialogYesNo
        message={t("academicHierarchy.removeSubject")}
        openStatus={removeSubjectDialogOpen}
        onClickNo={() => setRemoveSubjectDialogOpen(false)}
        onClickYes={handleRemoveDepartment}
      />
    </>
  );
};

export default Department;
